import { useEffect, useState } from "react";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal } from "react-bootstrap";
import { AssetSvg } from "../../assets";
import { useFunctionalityCallRecording } from "../../pages/hooks";
import { useFunctionalityCallTranscription } from "../../pages/hooks/functionalityCallTranscription";
import { CallTranscriptionItem } from "../../models/api/callTranscription";

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  callId?: string;
  title?: string;
};

export function ModalCallRecording(props: Props): JSX.Element {
  const { show, handleShow, callId, title } = props;
  const {
    LoadByCallId: LoadRecordingByCallId,
    LoadAccessUrl: LoadRecordingAccessUrl
  } = useFunctionalityCallRecording();
  const {
    LoadByCallId: LoadTranscriptionByCallId,
    LoadAccessUrl: LoadTranscriptionAccessUrl
  } = useFunctionalityCallTranscription();
  const [audioUrl, setAudioUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [recordingId, setRecordingId] = useState<string>("");
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [accessUrlExpiresAtUtc, setAccessUrlExpiresAtUtc] = useState<string>("");
  const [transcriptionText, setTranscriptionText] = useState<string>("");
  const [transcriptionTxtId, setTranscriptionTxtId] = useState<string>("");
  const [transcriptionCsvId, setTranscriptionCsvId] = useState<string>("");
  const [transcriptionLoading, setTranscriptionLoading] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState(false);
  const [transcriptionDownloadTxtLoading, setTranscriptionDownloadTxtLoading] = useState(false);
  const [transcriptionDownloadCsvLoading, setTranscriptionDownloadCsvLoading] = useState(false);
  const [transcriptionAccessUrl, setTranscriptionAccessUrl] = useState<string>("");
  const [transcriptionFormat, setTranscriptionFormat] = useState<string>("");
  const [showTranscriptionDownloadMenu, setShowTranscriptionDownloadMenu] = useState(false);

  useEffect(() => {
    if (!show || !callId) return;
    setIsLoading(true);
    setHasError(false);
    setAudioUrl("");
    setRecordingId("");
    setAccessUrlExpiresAtUtc("");
    LoadRecordingByCallId(callId)
      .then((data) => {
        const first = Array.isArray(data) ? data[0] : data;
        const url = first?.accessUrl ?? "";
        const id = first?.id ?? "";
        const expiresAt = first?.accessUrlExpiresAtUtc ?? "";
        setAudioUrl(url);
        setRecordingId(id);
        setAccessUrlExpiresAtUtc(expiresAt);
      })
      .catch(() => {
        setHasError(true);
      })
      .finally(() => setIsLoading(false));
  }, [LoadRecordingByCallId, callId, show]);

  useEffect(() => {
    if (!show || !callId) return;
    setTranscriptionLoading(true);
    setTranscriptionError(false);
    setTranscriptionText("");
    setTranscriptionTxtId("");
    setTranscriptionCsvId("");
    setTranscriptionAccessUrl("");
    setTranscriptionFormat("");
    LoadTranscriptionByCallId(callId)
      .then((data) => {
        const items = (Array.isArray(data) ? data : [data]).filter(
          Boolean
        ) as CallTranscriptionItem[];
        const txtItem =
          items.find(
            (item) => (item?.format ?? "").toLowerCase() === "txt"
          ) ?? null;
        const csvItem =
          items.find(
            (item) => (item?.format ?? "").toLowerCase() === "csv"
          ) ?? null;
        setTranscriptionTxtId(txtItem?.id ?? "");
        setTranscriptionCsvId(csvItem?.id ?? "");
        setTranscriptionAccessUrl(txtItem?.accessUrl ?? "");
        setTranscriptionFormat(txtItem?.format ?? "");
      })
      .catch(() => {
        setTranscriptionError(true);
      })
      .finally(() => setTranscriptionLoading(false));
  }, [LoadTranscriptionByCallId, callId, show]);

  useEffect(() => {
    if (show) return;
    setIsLoading(false);
    setHasError(false);
    setAudioUrl("");
    setRecordingId("");
    setAccessUrlExpiresAtUtc("");
    setDownloadLoading(false);
    setTranscriptionText("");
    setTranscriptionTxtId("");
    setTranscriptionCsvId("");
    setTranscriptionLoading(false);
    setTranscriptionError(false);
    setTranscriptionDownloadTxtLoading(false);
    setTranscriptionDownloadCsvLoading(false);
    setTranscriptionAccessUrl("");
    setTranscriptionFormat("");
    setShowTranscriptionDownloadMenu(false);
  }, [show]);

  useEffect(() => {
    if (!show || !callId || !accessUrlExpiresAtUtc) return;
    const expiresAtMs = Date.parse(accessUrlExpiresAtUtc);
    if (Number.isNaN(expiresAtMs)) return;

    const now = Date.now();
    const refreshInMs = Math.max(0, expiresAtMs - now - 5000);
    const timer = window.setTimeout(() => {
      LoadRecordingByCallId(callId).then((data) => {
        const first = Array.isArray(data) ? data[0] : data;
        const url = first?.accessUrl ?? "";
        const id = first?.id ?? "";
        const expiresAt = first?.accessUrlExpiresAtUtc ?? "";
        setAudioUrl(url);
        setRecordingId(id);
        setAccessUrlExpiresAtUtc(expiresAt);
      }).catch(() => {
        setHasError(true);
      });
    }, refreshInMs);

    return () => window.clearTimeout(timer);
  }, [LoadRecordingByCallId, accessUrlExpiresAtUtc, callId, show]);

  const canDownload = Boolean(recordingId);

  const handleDownload = async () => {
    if (!recordingId || downloadLoading) return;
    setDownloadLoading(true);
    try {
      const data = await LoadRecordingAccessUrl(recordingId, true, 30);
      const url = data?.url ?? "";
      if (url) {
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.rel = "noopener";
        anchor.target = "_blank";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      }
    } catch {
      // no-op; error message is handled by backend errors and user can retry
    } finally {
      setDownloadLoading(false);
    }
  };

  const canDownloadTxtTranscription = Boolean(transcriptionTxtId);
  const canDownloadCsvTranscription = Boolean(transcriptionCsvId);

  const handleTranscriptionDownload = async (id: string, type: "txt" | "csv") => {
    if (!id) return;
    if (type === "txt" && transcriptionDownloadTxtLoading) return;
    if (type === "csv" && transcriptionDownloadCsvLoading) return;
    if (type === "txt") setTranscriptionDownloadTxtLoading(true);
    if (type === "csv") setTranscriptionDownloadCsvLoading(true);
    try {
      const data = await LoadTranscriptionAccessUrl(id, true, 30);
      const url = data?.url ?? "";
      if (url) {
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.rel = "noopener";
        anchor.target = "_blank";
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      }
    } catch {
      // no-op; error message is handled by backend errors and user can retry
    } finally {
      if (type === "txt") setTranscriptionDownloadTxtLoading(false);
      if (type === "csv") setTranscriptionDownloadCsvLoading(false);
    }
  };

  const canViewTranscription =
    Boolean(transcriptionAccessUrl) &&
    (transcriptionFormat || "").toLowerCase() === "txt";

  const renderTranscriptionDownloadMenu = () => {
    if (!canDownloadTxtTranscription && !canDownloadCsvTranscription) {
      return null;
    }

    return (
      <div className="call-transcription-download">
        <button
          type="button"
          className="btn btn-primary d-flex align-items-center call-transcription-download__trigger"
          onClick={() => setShowTranscriptionDownloadMenu((prev) => !prev)}
          disabled={transcriptionDownloadTxtLoading || transcriptionDownloadCsvLoading}
        >
          <span style={{ lineHeight: 0, marginRight: 6 }}>
            <AssetSvg.Download style={{ width: 16, height: 16 }} />
          </span>
          Download
          <span style={{ marginLeft: 6, fontSize: 12 }}>▼</span>
        </button>
        {showTranscriptionDownloadMenu && (
          <div
            className="call-transcription-download__menu"
          >
            {canDownloadTxtTranscription && (
              <button
                type="button"
                className="btn btn-link w-100 text-left call-transcription-download__item"
                onClick={() => {
                  setShowTranscriptionDownloadMenu(false);
                  handleTranscriptionDownload(transcriptionTxtId, "txt");
                }}
                disabled={transcriptionDownloadTxtLoading}
              >
                Download TXT
              </button>
            )}
            {canDownloadCsvTranscription && (
              <button
                type="button"
                className="btn btn-link w-100 text-left call-transcription-download__item"
                onClick={() => {
                  setShowTranscriptionDownloadMenu(false);
                  handleTranscriptionDownload(transcriptionCsvId, "csv");
                }}
                disabled={transcriptionDownloadCsvLoading}
              >
                Download CSV
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const handleLoadTranscriptionText = async () => {
    if (!canViewTranscription || transcriptionLoading) return;
    setTranscriptionLoading(true);
    setTranscriptionError(false);
    try {
      const res = await fetch(transcriptionAccessUrl);
      const text = await res.text();
      setTranscriptionText(text);
    } catch {
      setTranscriptionError(true);
    } finally {
      setTranscriptionLoading(false);
    }
  };

  useEffect(() => {
    if (!show) return;
    if (!canViewTranscription) return;
    if (transcriptionText) return;
    handleLoadTranscriptionText();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, canViewTranscription, transcriptionAccessUrl]);

  return (
    <Modal
      show={show}
      onHide={() => handleShow(false)}
      id="callRecordingModal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{title ?? "Call Media"}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="call-media-modal-body">
        <div className="accordion" id="call-media-panels">
          <div className="card">
            <div className="card-header px-0 py-1" id="headingCallRecording">
              <button
                className="btn btn-link btn-block text-left text-decoration-none text-site siteAccordion"
                type="button"
                data-toggle="collapse"
                data-target="#call-recording-panel"
                aria-expanded="true"
                aria-controls="call-recording-panel"
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>Call Recording</div>
                  <div>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className="closed-panel-icon"
                    />
                    <FontAwesomeIcon
                      icon={faChevronUp}
                      className="open-panel-icon"
                    />
                  </div>
                </div>
              </button>
            </div>
            <div
              className="collapse show"
              id="call-recording-panel"
              aria-labelledby="headingCallRecording"
            >
              <div className="card-body px-3 py-2 call-recording-panel">
                {isLoading ? (
                  <div className="text-muted">Loading recording...</div>
                ) : hasError ? (
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <div className="text-danger">Failed to load recording.</div>
                    <button
                      type="button"
                      className="btn btn-primary d-flex align-items-center"
                      style={{ borderRadius: 10, padding: 10 }}
                      title="Download"
                      onClick={handleDownload}
                      disabled={!canDownload || downloadLoading}
                    >
                      <span style={{ lineHeight: 0 }}>
                        <AssetSvg.Download style={{ width: 16, height: 16 }} />
                      </span>
                    </button>
                  </div>
                ) : audioUrl ? (
                  <div className="d-flex align-items-center gap-2">
                    <audio
                      controls
                      controlsList="nodownload"
                      className="flex-grow-1 mr-3"
                      style={{ marginTop: 8 }}
                    >
                      <source src={audioUrl} />
                      Your browser does not support the audio element.
                    </audio>
                    <button
                      type="button"
                      className="btn btn-primary d-flex align-items-center mt-2"
                      style={{ borderRadius: 10, padding: 12 }}
                      title="Download"
                      onClick={handleDownload}
                      disabled={!canDownload || downloadLoading}
                    >
                      <span className="" style={{ lineHeight: 0 }}>
                        <AssetSvg.Download style={{ width: 16, height: 16 }} />
                      </span>
                    </button>
                  </div>
                ) : (
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <div className="text-muted">No recording is available for this call.</div>
                    <button
                      type="button"
                      className="btn btn-primary d-flex align-items-center"
                      style={{ borderRadius: 10, padding: 10 }}
                      title="Download"
                      onClick={handleDownload}
                      disabled={!canDownload || downloadLoading}
                    >
                      <span style={{ lineHeight: 0 }}>
                        <AssetSvg.Download style={{ width: 16, height: 16 }} />
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header px-0 py-1" id="headingCallTranscription">
              <button
                className="btn btn-link btn-block text-left text-decoration-none text-site siteAccordion collapsed"
                type="button"
                data-toggle="collapse"
                data-target="#call-transcription-panel"
                aria-expanded="false"
                aria-controls="call-transcription-panel"
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>Call Transcription</div>
                  <div>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className="closed-panel-icon"
                    />
                    <FontAwesomeIcon
                      icon={faChevronUp}
                      className="open-panel-icon"
                    />
                  </div>
                </div>
              </button>
            </div>
            <div
              className="collapse"
              id="call-transcription-panel"
              aria-labelledby="headingCallTranscription"
            >
              <div className="card-body px-3 py-2 call-transcription-panel">
                <div className="call-transcription-panel__inner">
                  {transcriptionLoading ? (
                    <div className="text-muted">Loading transcription...</div>
                  ) : transcriptionError ? (
                    <div className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                      <div className="text-danger">Failed to load transcription.</div>
                      {renderTranscriptionDownloadMenu()}
                    </div>
                  ) : transcriptionText ? (
                    <>
                      <div
                        style={{
                          whiteSpace: "pre-wrap",
                          maxHeight: 240,
                          overflow: "auto",
                          padding: 12,
                          borderRadius: 8,
                          border: "1px solid rgba(0,0,0,0.1)",
                          background: "rgba(0,0,0,0.02)",
                        }}
                      >
                        {transcriptionText}
                      </div>
                      <div className="d-flex align-items-center justify-content-between gap-2 mt-3 flex-wrap">
                        <div className="text-muted">Transcription file is available.</div>
                        {renderTranscriptionDownloadMenu()}
                      </div>
                    </>
                  ) : transcriptionAccessUrl ? (
                    <>
                      <div className="text-muted mb-2">
                        Transcription file is available.
                      </div>
                      <div className="d-flex align-items-center w-100 justify-content-between gap-3 flex-wrap">
                        <button
                          type="button"
                          className="btn btn-secondary d-flex align-items-center"
                          onClick={handleLoadTranscriptionText}
                          disabled={!canViewTranscription || transcriptionLoading}
                          title={
                            canViewTranscription
                              ? "Load transcription text"
                              : "Preview is available only for TXT"
                          }
                        >
                          View TXT
                        </button>
                        {renderTranscriptionDownloadMenu()}
                      </div>
                    </>
                  ) : (
                    <div className="text-muted">
                      No transcription is available for this call.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          className="btn btn-secondary text-uppercase"
          onClick={() => handleShow(false)}
        >
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
}
